export type ContentType = "drama" | "actor" | "news";

export interface BodySection {
  heading: string;
  paragraphs: string[];
}

export interface BaseItem {
  id: string;
  type: ContentType;
  title: string;
  subtitle: string;
  excerpt: string;
  cover: string;
  date: string;
  tags: string[];
  body: BodySection[];
}

export interface DramaItem extends BaseItem {
  type: "drama";
  chapter: string;
  duration: string;
  views: string;
}

export interface ActorItem extends BaseItem {
  type: "actor";
  role: string;
  roleType: string;
  birthday: string;
  stills: string[];
}

export interface NewsItem extends BaseItem {
  type: "news";
  source: string;
}

export type ContentItem = DramaItem | ActorItem | NewsItem;

export const siteInfo = {
  name: "嫂子的诱惑",
  englishName: "THE TEMPTATION",
  slogan: "一屋人家，两代心事",
  description:
    "嫂子的诱惑为您提供完整的电视剧剧情介绍、演员信息和影视资讯，助您深入了解剧情发展，享受精彩电视剧内容。",
  keywords:
    "嫂子的诱惑,嫂子的诱惑电视剧,嫂子的诱惑剧情,电视剧全集,影视资讯,剧情介绍",
  intro:
    "《嫂子的诱惑》以沈家三代人的日常为切口，讲一个关于体面、隐忍与和解的故事。本站以剧集为核心，整理每一集的关键情节、人物关系与幕后资讯，帮助你在追剧时看得更清楚，也看得更从容。",
  author: {
    name: "苏念",
    title: "站长 / 剧评人",
    bio: "写了十二年家庭剧剧评，相信最动人的戏剧冲突永远藏在饭桌上。每周更新《嫂子的诱惑》剧情拆解与演员访谈，陪你把每一集看完。",
    avatar:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20thoughtful%20Chinese%20woman%20writer%20in%20her%20thirties%20wearing%20a%20soft%20cream%20knit%20sweater%2C%20sitting%20in%20a%20warm%20book-filled%20study%2C%20cinematic%20soft%20window%20light%2C%20muted%20amber%20and%20burgundy%20tones%2C%20editorial%20portrait%20photography%2C%20shallow%20depth%20of%20field&width=600&height=600&seq=author-su-nian-01&orientation=squarish",
  },
  contact: {
    email: "hello@saozideyouhuo.com",
    workTime: "周一至周五 10:00 - 18:00",
    address: "内容编辑部 · 线上办公",
  },
};

export const categoryMeta = {
  剧情介绍: {
    slug: "剧情介绍",
    type: "drama" as ContentType,
    title: "嫂子的诱惑剧情介绍全集",
    kicker: "剧情档案",
    intro:
      "从第1集到最后一集，《嫂子的诱惑》把沈家的餐桌、走廊与深夜客厅拍成了一个个无声的战场。以下为已整理的剧情章节，包含完整剧情摘要与关键转折提示。",
    filters: ["全部章节", "前期铺垫", "矛盾升级", "真相揭露", "和解收束"],
    sortOptions: ["按章节顺序", "按更新倒序", "按篇幅排序"],
    empty: "暂无可展示的剧情章节",
  },
  演员资料: {
    slug: "演员资料",
    type: "actor" as ContentType,
    title: "嫂子的诱惑演员资料大全",
    kicker: "演员档案",
    intro:
      "六位演员，六种表演方法。苏婉的克制、沈嘉言的沉默、沈母的锋利，共同撑起了《嫂子的诱惑》的情感张力。这里整理了主要与配角演员资料、饰演角色与代表作。",
    filters: ["全部角色", "主要角色", "配角", "特别出演"],
    sortOptions: ["按角色重要度", "按姓氏排序"],
    empty: "暂无符合条件的演员资料",
  },
  影视资讯: {
    slug: "影视资讯",
    type: "news" as ContentType,
    title: "嫂子的诱惑影视资讯动态",
    kicker: "资讯动态",
    intro:
      "收视表现、幕后花絮、主创专访与取景地盘点，关于《嫂子的诱惑》的台前幕后都在这里。内容按时间排序，方便你快速掌握剧集最新动态。",
    filters: ["全部资讯", "收视动态", "主创专访", "幕后花絮"],
    sortOptions: ["按时间倒序", "按时间正序"],
    empty: "暂无相关资讯",
  },
};

export const dramas: DramaItem[] = [
  {
    id: "drama-01",
    type: "drama",
    title: "第1集 · 婚礼上的暗涌",
    subtitle: "沈家大宅，一场看似完美的婚礼",
    chapter: "第1集",
    duration: "45 分钟",
    views: "128.6 万",
    excerpt:
      "沈嘉言与苏婉的婚礼在沈家老宅举行。席间一句无心的祝酒词，让沈母第一次认真打量这位新进门的儿媳；而苏婉在整理礼金时发现的那张旧照片，成了她此后所有不安的起点。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20Chinese%20family%20drama%20still%20of%20an%20elegant%20old%20mansion%20wedding%20banquet%2C%20long%20red%20table%20with%20warm%20lantern%20light%2C%20deep%20crimson%20and%20gold%20color%20grading%2C%20soft%20film%20lighting%2C%20shallow%20depth%20of%20field%2C%20editorial%20cinematography&width=800&height=500&seq=drama-cover-01&orientation=landscape",
    date: "2026-08-02",
    tags: ["剧情介绍", "矛盾升级", "沈家", "婚礼"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "故事从一场雨后的婚礼开始。沈家老宅被装点得喜气洋洋，红绸从门廊一直垂到庭院。沈嘉言站在台阶上迎客，神情温和却始终有一丝走神。苏婉穿着并不算华丽的婚纱，在伴娘的搀扶下走进这栋她只来过两次的房子。",
          "宴席过半，沈母举杯致辞，说到“进了沈家的门，就要守沈家的规矩”时，语气轻描淡写，却让整桌人短暂沉默。苏婉笑着应下，低头时手指在杯沿上停了很久。",
          "夜里，宾客散尽。苏婉在整理礼金簿时，从一本旧册子里掉出一张褪色的照片——照片上的女孩与沈嘉言并肩站着，背面写着一个陌生的名字。她把照片放回原处，没有问。这是她第一次选择沉默，也是全剧所有误解的开端。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "婚礼宴席上三人同桌的长镜头，是全剧人物关系的第一次集中展示：沈母端坐主位，沈嘉言居于中间，苏婉始终微微侧身，像随时准备退让。",
          "结尾处的旧照片，为后续“沈嘉言的过去”埋下伏笔。剧集没有给出任何解释，只用一盏逐渐熄灭的门灯收尾。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "沈嘉言：沈家长子，建筑设计事务所合伙人，习惯用沉默处理冲突。苏婉：沈嘉言之妻，婚前是中学语文老师，擅长察言观色。沈母：沈家实际的决策者，把家族的体面看得比什么都重。",
        ],
      },
    ],
  },
  {
    id: "drama-02",
    type: "drama",
    title: "第2集 · 一杯加了糖的茶",
    subtitle: "婆媳之间的第一次正面交锋",
    chapter: "第2集",
    duration: "44 分钟",
    views: "116.2 万",
    excerpt:
      "苏婉按照沈母的口味煮了茶，却因为多放了一勺糖被当众纠正。她默默重新煮了一壶，也第一次意识到，这栋房子里的每一件小事都有规矩，而这些规矩从来不写在纸上。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20Chinese%20tea%20ceremony%20on%20a%20dark%20wooden%20table%20inside%20a%20traditional%20mansion%2C%20porcelain%20teacup%20with%20rising%20steam%2C%20warm%20amber%20lamplight%2C%20deep%20burgundy%20curtains%20in%20background%2C%20moody%20film%20photography%2C%20soft%20focus&width=800&height=500&seq=drama-cover-02&orientation=landscape",
    date: "2026-08-03",
    tags: ["剧情介绍", "前期铺垫", "婆媳", "沈家"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "婚后的第一个清晨，苏婉起得很早。她照着记忆里沈母的习惯煮茶，水开后加了一勺糖——在她的娘家，这是待客的礼数。",
          "茶端上桌，沈母尝了一口，放下杯子说：“我们家的茶，不放糖。”语气不重，却让餐桌上的空气凝固。沈嘉言想开口，被苏婉轻轻按住手背。",
          "苏婉重新煮了一壶。这一次她把糖罐收进柜子最里层，动作很慢，像是在把某种东西一起收起来。傍晚她给母亲打电话，只说了一句“他们都对我很好”。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "糖罐成为全剧反复出现的意象。此后每一次家庭矛盾，镜头都会不经意扫过那个柜子。",
          "沈母并非脸谱化的恶人。她在夜里独自把剩下的甜茶喝完，这个小动作让角色多了一层复杂。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "本集确立了婆媳关系的基本格局：不吵、不闹、不撕破脸，所有冲突都用礼貌包裹。沈嘉言在其中的失语，是后续夫妻裂痕的根源。",
        ],
      },
    ],
  },
  {
    id: "drama-03",
    type: "drama",
    title: "第3集 · 旧照片里的秘密",
    subtitle: "一个被全家刻意忘记的名字",
    chapter: "第3集",
    duration: "46 分钟",
    views: "109.8 万",
    excerpt:
      "苏婉在阁楼整理旧物时，发现了一整箱被剪去一角的合影。照片里的女孩叫林知夏，是沈嘉言大学时代的恋人，也是沈家绝口不提的一段往事。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20an%20old%20attic%20filled%20with%20dusty%20photo%20albums%2C%20a%20womans%20hand%20holding%20a%20faded%20black%20and%20white%20photograph%2C%20single%20window%20light%20beam%20cutting%20through%20dust%2C%20warm%20sepia%20and%20crimson%20tones%2C%20melancholic%20film%20aesthetic&width=800&height=500&seq=drama-cover-03&orientation=landscape",
    date: "2026-08-04",
    tags: ["剧情介绍", "真相揭露", "往事", "悬念"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "沈家阁楼常年上锁。苏婉在找一只旧箱子时被允许进去，条件是“只拿东西，不要乱翻”。她在最里面的纸箱里看到一整叠合影，每一张都被剪去了一角。",
          "被剪掉的那个人，是林知夏——沈嘉言的大学同学，也是沈母曾经最中意的儿媳人选。照片背面留着她的字迹：“等你看完这场雪。”",
          "苏婉把照片放回箱子，却在楼梯口遇到沈母。沈母没有发火，只说：“有些事知道了，对谁都不好。”这句话让苏婉第一次感到，自己或许嫁进了一个有秘密的家。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "阁楼一场戏用光极为克制，一束窗光把苏婉的脸分成明暗两半，象征她从此进入这个家的“暗面”。",
          "林知夏这个名字首次出现，成为全剧中段最大的悬念。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "沈母对旧事的回避并非出于恶意，而是出于恐惧。她害怕的从来不是苏婉知道，而是沈嘉言重新想起。",
        ],
      },
    ],
  },
  {
    id: "drama-04",
    type: "drama",
    title: "第4集 · 深夜未归的人",
    subtitle: "第一次真正意义上的等待",
    chapter: "第4集",
    duration: "45 分钟",
    views: "98.4 万",
    excerpt:
      "沈嘉言连续三晚深夜才归，手机永远扣在桌面上。苏婉没有追问，只是在客厅留了一盏灯。第四晚，她在阳台上看见楼下有人替他开车门，是一个女人的背影。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20night%20still%20of%20a%20woman%20standing%20alone%20at%20a%20lit%20window%20looking%20down%20at%20a%20rainy%20city%20street%2C%20single%20warm%20lamp%20glow%20inside%2C%20dark%20crimson%20and%20charcoal%20tones%2C%20atmospheric%20film%20photography%2C%20reflective%20mood&width=800&height=500&seq=drama-cover-04&orientation=landscape",
    date: "2026-08-05",
    tags: ["剧情介绍", "矛盾升级", "夫妻", "误会"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "沈嘉言的事务所接到一个大项目，他连续加班。苏婉每晚在客厅留一盏灯，灯亮到凌晨一点。",
          "第四晚，苏婉在阳台收衣服，看见楼下停着一辆黑色轿车。一个穿长风衣的女人替沈嘉言拉开车门，两人在车边说了几句话，沈嘉言罕见地笑了。",
          "苏婉退回屋里，把灯关掉。沈嘉言进门时，客厅一片黑。他站在门口愣了几秒，才摸到开关。这一集结束在两个人的对视里，谁都没有说话。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "“留灯”到“关灯”的动作变化，是全剧最经济的一次情绪表达，没有一句台词，观众却能读懂苏婉的退让与心凉。",
          "那个穿风衣的女人正是林知夏，剧集在此刻并没有点明，只给了观众一个背影。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "夫妻关系从这一集开始出现裂缝。值得注意的是，裂缝不是由背叛造成，而是由“都不说”造成。",
        ],
      },
    ],
  },
  {
    id: "drama-05",
    type: "drama",
    title: "第5集 · 餐桌上的对峙",
    subtitle: "一顿没能吃完的家宴",
    chapter: "第5集",
    duration: "47 分钟",
    views: "134.1 万",
    excerpt:
      "沈家每月一次的家宴上，沈母当众提起“该要个孩子了”。苏婉第一次没有顺从，她说出了自己想做回老师的打算。整张餐桌安静下来，沈嘉言把筷子放下了。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20tense%20Chinese%20family%20dinner%20table%20with%20uneaten%20dishes%20and%20chopsticks%20resting%2C%20empty%20chairs%2C%20warm%20overhead%20lamp%2C%20deep%20wine%20red%20tablecloth%2C%20dramatic%20side%20lighting%2C%20moody%20editorial%20cinematography&width=800&height=500&seq=drama-cover-05&orientation=landscape",
    date: "2026-08-06",
    tags: ["剧情介绍", "矛盾升级", "家宴", "女性成长"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "沈家的家宴有固定流程：先敬长辈，再谈家事。这天沈母在第二道菜上桌时提起孩子的事，语气像是在安排一件早该完成的工作。",
          "苏婉放下汤勺，说自己想回学校教书，孩子的事想再等两年。这是她进门以来第一次明确表达不同意见。",
          "沈母的脸色变了。沈嘉言试图圆场，说“再商量”，苏婉却看着他，问了一句：“你也觉得我不该有自己的事吗？”没有人回答。这场家宴在第三道菜之前结束。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "全剧台词密度最高的一场戏，五个人的性格在一顿饭里全部暴露。",
          "苏婉的“第一次拒绝”是角色弧光的关键转折，她不再只依靠沉默换取平静。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "沈母的强势来自她对家族延续的执念；苏婉的坚持来自她对自己身份的确认。这场对峙没有赢家，只有更清楚的彼此。",
        ],
      },
    ],
  },
  {
    id: "drama-06",
    type: "drama",
    title: "第6集 · 雨夜的真心话",
    subtitle: "林知夏正式登场",
    chapter: "第6集",
    duration: "46 分钟",
    views: "142.7 万",
    excerpt:
      "暴雨夜，林知夏以项目合作方的身份出现在沈家。她的从容与熟悉让苏婉瞬间明白了那张旧照片的意义。两个女人在厨房里第一次单独相处，说出的话都客气体面，却句句带锋。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20rainy%20night%20scene%20of%20two%20elegant%20Chinese%20women%20talking%20in%20a%20warm%20kitchen%20with%20rain%20streaming%20down%20tall%20windows%2C%20amber%20light%20and%20deep%20red%20ceramics%2C%20tense%20quiet%20atmosphere%2C%20film%20noir%20color%20grading&width=800&height=500&seq=drama-cover-06&orientation=landscape",
    date: "2026-08-07",
    tags: ["剧情介绍", "真相揭露", "林知夏", "对手戏"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "暴雨让整条街停电。林知夏带着项目文件来到沈家，说是顺路。她的出现让沈母罕见地失态，茶杯磕在托盘上发出一声轻响。",
          "厨房里只剩两个女人。林知夏主动提起“嘉言以前最怕打雷”，苏婉回了一句“他现在也是”，然后低头切水果。刀落在砧板上的声音，比窗外的雨还清楚。",
          "林知夏临走前在玄关停了一下，说：“你比我适合这里。”苏婉没有接话。她关上门，靠在门板上，很长时间没有动。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "两场雨戏交叉剪辑，一场在客厅，一场在厨房，把“同一个屋檐下的三种立场”呈现得非常清楚。",
          "林知夏并非反派。她的体面与克制，反而让苏婉更难找到一个可以责怪的对象。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "林知夏与沈嘉言之间已经结束，但沈母心里的“如果”从未结束。这才是苏婉真正的对手。",
        ],
      },
    ],
  },
  {
    id: "drama-07",
    type: "drama",
    title: "第7集 · 谁在说谎",
    subtitle: "一张被修改过的体检报告",
    chapter: "第7集",
    duration: "48 分钟",
    views: "137.3 万",
    excerpt:
      "苏婉在整理抽屉时发现了一份被替换过的体检报告。沈母隐瞒了自己的病情，也隐瞒了这个家真正的危机。所有此前的苛责，在这一刻有了另一种解释。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20close-up%20still%20of%20a%20medical%20report%20on%20a%20dark%20wooden%20desk%20beside%20reading%20glasses%20and%20a%20cup%20of%20cold%20tea%2C%20dramatic%20single%20lamp%20light%2C%20wine%20red%20and%20deep%20ochre%20tones%2C%20quiet%20tense%20mood%2C%20prestige%20drama%20cinematography&width=800&height=500&seq=drama-cover-07&orientation=landscape",
    date: "2026-08-08",
    tags: ["剧情介绍", "真相揭露", "沈母", "秘密"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "苏婉替沈母找医保卡，在抽屉最底层发现两份体检报告。日期只差一周，结论却完全不同。",
          "真相是沈母早在半年前就查出心脏问题，需要手术。她瞒着所有人，一边催着儿子儿媳要孩子，一边在深夜独自数药片。",
          "苏婉没有揭穿。第二天清晨她照常煮茶，只是在沈母的杯子里换成了温水。沈母看了她一眼，什么也没说。这是全剧第一次，两个女人之间出现了默契。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "本集是全剧的情绪转折点，此前所有的“控制欲”都被重新解释为“时间不够的焦虑”。",
          "苏婉选择沉默守护，而不是当众揭穿，这个决定让她真正成为这个家的一员。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "沈母的角色弧光从这一集开始逆转。她不是要压住苏婉，而是急着在有生之年把家交出去。",
        ],
      },
    ],
  },
  {
    id: "drama-08",
    type: "drama",
    title: "第8集 · 迟来的和解",
    subtitle: "手术室外的三个小时",
    chapter: "第8集",
    duration: "50 分钟",
    views: "156.9 万",
    excerpt:
      "沈母的手术安排在初冬的清晨。三个小时的等待里，沈嘉言终于说出当年与林知夏分手的真相，苏婉也第一次讲起自己为什么总是选择沉默。走廊尽头的灯亮起来时，这个家才开始真正说话。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20hospital%20corridor%20at%20dawn%20with%20warm%20morning%20light%20through%20tall%20windows%2C%20two%20silhouetted%20figures%20sitting%20on%20a%20bench%2C%20quiet%20hopeful%20mood%2C%20soft%20golden%20and%20burgundy%20tones%2C%20prestige%20drama%20cinematography&width=800&height=500&seq=drama-cover-08&orientation=landscape",
    date: "2026-08-09",
    tags: ["剧情介绍", "和解收束", "大结局", "家人"],
    body: [
      {
        heading: "剧情梗概",
        paragraphs: [
          "手术定在清晨七点。沈母进手术室前，把家里的钥匙放在苏婉手里，只说了一句“糖罐在第二个柜子”。",
          "三个小时的等待里，沈嘉言第一次完整讲起当年：林知夏要去外地，他没有挽留，因为他从没学会怎么留人。苏婉则讲起自己从小在争吵里长大，所以只会用沉默换太平。",
          "灯亮起来的时候，医生说手术顺利。沈嘉言在走廊里抱住苏婉，这个拥抱迟到了整整八集。结尾，一家人回到老宅吃饭，桌上多了一道放糖的茶。",
        ],
      },
      {
        heading: "本集看点",
        paragraphs: [
          "钥匙与糖罐的呼应，是全剧最动人的收尾设计：交接的不是权力，而是信任。",
          "结尾没有刻意的大团圆，沈母仍需长期复查，苏婉也回到了学校。生活继续，只是这个家学会了说话。",
        ],
      },
      {
        heading: "人物关系提示",
        paragraphs: [
          "大结局没有解决所有问题，但让每个人都有了表达的能力。这也是本剧最想说的：家庭的和解，往往从一次坦白开始。",
        ],
      },
    ],
  },
];

export const actors: ActorItem[] = [
  {
    id: "actor-01",
    type: "actor",
    title: "林婉清",
    subtitle: "饰 苏婉",
    role: "苏婉",
    roleType: "主要角色",
    birthday: "1992-04-18",
    excerpt:
      "饰演沈嘉言之妻苏婉。以极少的台词和大量的眼神完成角色弧光，把一个隐忍、克制又不甘的儿媳演得层次分明。",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20portrait%20of%20a%20Chinese%20actress%20in%20her%20early%20thirties%20with%20soft%20wavy%20dark%20hair%20and%20calm%20expressive%20eyes%2C%20wearing%20a%20cream%20silk%20blouse%2C%20warm%20neutral%20studio%20backdrop%20with%20subtle%20burgundy%20tone%2C%20editorial%20beauty%20photography%2C%20soft%20diffused%20light%2C%20squarish%20composition&width=600&height=600&seq=actor-lin-wanqing-01&orientation=squarish",
    date: "2026-08-02",
    tags: ["演员资料", "主演", "苏婉", "林婉清"],
    stills: [
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20Chinese%20woman%20in%20a%20cream%20knit%20sweater%20standing%20by%20a%20shuttered%20window%20in%20an%20old%20mansion%2C%20warm%20afternoon%20light%2C%20quiet%20melancholic%20expression%2C%20burgundy%20curtains%2C%20soft%20film%20grain&width=800&height=500&seq=actor-lin-wanqing-still-01&orientation=landscape",
    ],
    body: [
      {
        heading: "演员简介",
        paragraphs: [
          "林婉清，1992年生，中央戏剧学院表演系毕业。早期以话剧舞台为主，2018年凭《长夜将尽》进入大众视野，此后多出演现实主义题材中的女性角色。",
          "在《嫂子的诱惑》中，她主动要求删减台词，把大量情绪交给眼神和手部动作。导演评价她“能让沉默有重量”。",
        ],
      },
      {
        heading: "角色解读",
        paragraphs: [
          "苏婉不是传统意义上的“受气媳妇”。她的隐忍是一种策略，也是一种自我保护。林婉清在表演中刻意保留了角色身上的倔强：低头不代表屈服，只是还没到说话的时候。",
          "第5集餐桌对峙那场戏，她选择在说完台词后直视对面，眼神不闪躲，这个细节让角色的转变显得可信。",
        ],
      },
      {
        heading: "代表作品",
        paragraphs: [
          "《长夜将尽》饰 程雪；《春潮里》饰 陈曼；《嫂子的诱惑》饰 苏婉；舞台剧《雷雨》饰 繁漪。",
        ],
      },
    ],
  },
  {
    id: "actor-02",
    type: "actor",
    title: "陈亦舟",
    subtitle: "饰 沈嘉言",
    role: "沈嘉言",
    roleType: "主要角色",
    birthday: "1988-11-06",
    excerpt:
      "饰演沈家长子沈嘉言。全剧大部分时间都在“沉默”，却用眼神完成了一个男人从逃避到承担的全过程。",
    cover:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20Chinese%20actor%20in%20his%20late%20thirties%20with%20short%20dark%20hair%20and%20a%20thoughtful%20serious%20expression%2C%20wearing%20a%20charcoal%20wool%20coat%20over%20a%20white%20shirt%2C%20warm%20neutral%20studio%20background%2C%20editorial%20portrait%20photography%2C%20soft%20directional%20light%2C%20muted%20amber%20tones&width=600&height=600&seq=actor-chen-yizhou-01&orientation=squarish",
    date: "2026-08-02",
    tags: ["演员资料", "主演", "沈嘉言", "陈亦舟"],
    stills: [
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20Chinese%20man%20in%20a%20dark%20coat%20standing%20on%20the%20steps%20of%20an%20old%20mansion%20at%20dusk%2C%20warm%20window%20glow%20behind%20him%2C%20restrained%20melancholic%20mood%2C%20deep%20burgundy%20and%20amber%20grading%2C%20film%20photography&width=800&height=500&seq=actor-chen-yizhou-still-01&orientation=landscape",
    ],
    body: [
      {
        heading: "演员简介",
        paragraphs: [
          "陈亦舟，1988年生，北京电影学院毕业。以年代剧起家，擅长塑造内敛、有分寸感的男性角色，近年转向家庭题材，被称为“最会演沉默的男演员”。",
          "《嫂子的诱惑》是他与导演的第二次合作。为了贴近建筑设计师的身份，他提前两个月学习手绘图纸。",
        ],
      },
      {
        heading: "角色解读",
        paragraphs: [
          "沈嘉言的问题不在于不爱，而在于他习惯了让母亲替自己做决定。陈亦舟在处理这个角色时，刻意让他在每场冲突中都“慢半拍”，直到大结局那场走廊戏才第一次主动开口。",
        ],
      },
      {
        heading: "代表作品",
        paragraphs: [
          "《江城旧事》饰 顾长川；《归途列车》饰 罗一鸣；《嫂子的诱惑》饰 沈嘉言。",
        ],
      },
    ],
  },
  {
    id: "actor-03",
    type: "actor",
    title: "高芷宁",
    subtitle: "饰 沈母",
    role: "沈母",
    roleType: "主要角色",
    birthday: "1965-03-12",
    excerpt:
      "饰演沈家当家主母。她把“体面”演成了一种呼吸方式，直到第7集才让观众看见强硬背后的恐惧。",
    cover:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20distinguished%20Chinese%20older%20actress%20in%20her%20late%20fifties%20with%20neat%20grey-streaked%20chignon%20and%20piercing%20composed%20eyes%2C%20wearing%20a%20deep%20burgundy%20qipao%20collar%2C%20warm%20neutral%20studio%20backdrop%2C%20editorial%20portrait%20photography%2C%20soft%20rembrandt%20lighting&width=600&height=600&seq=actor-gao-zhining-01&orientation=squarish",
    date: "2026-08-03",
    tags: ["演员资料", "主演", "沈母", "高芷宁"],
    stills: [
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20an%20elegant%20older%20Chinese%20woman%20sitting%20alone%20at%20the%20head%20of%20a%20long%20dining%20table%20in%20a%20dim%20mansion%2C%20single%20warm%20lamp%2C%20deep%20wine%20red%20and%20ochre%20tones%2C%20restrained%20authoritative%20mood%2C%20prestige%20drama%20cinematography&width=800&height=500&seq=actor-gao-zhining-still-01&orientation=landscape",
    ],
    body: [
      {
        heading: "演员简介",
        paragraphs: [
          "高芷宁，1965年生，国家一级演员，话剧舞台出身。四十余年演艺生涯中塑造过大量母亲与家族长辈形象，被观众称为“最有压迫感的母亲”。",
        ],
      },
      {
        heading: "角色解读",
        paragraphs: [
          "沈母的强硬来自时间焦虑。高芷宁在表演时给角色设计了一个小动作：说话前先把袖口整理一下，像是在把情绪也一并抚平。到了第7集，这个动作第一次没有完成。",
        ],
      },
      {
        heading: "代表作品",
        paragraphs: [
          "《秋水长天》饰 顾母；《大宅门里》饰 二奶奶；《嫂子的诱惑》饰 沈母。",
        ],
      },
    ],
  },
  {
    id: "actor-04",
    type: "actor",
    title: "周慕辰",
    subtitle: "饰 沈明远",
    role: "沈明远",
    roleType: "配角",
    birthday: "1995-07-22",
    excerpt:
      "饰演沈家次子沈明远。看似不参与家务事的“局外人”，却是全剧第一个把真相说出口的人。",
    cover:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Chinese%20actor%20in%20his%20late%20twenties%20with%20relaxed%20styled%20dark%20hair%20and%20a%20slight%20wry%20smile%2C%20wearing%20a%20rust%20colored%20sweater%2C%20warm%20neutral%20studio%20background%2C%20editorial%20portrait%20photography%2C%20soft%20window%20light&width=600&height=600&seq=actor-zhou-muchen-01&orientation=squarish",
    date: "2026-08-04",
    tags: ["演员资料", "配角", "沈明远", "周慕辰"],
    stills: [
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20young%20Chinese%20man%20leaning%20against%20a%20mansion%20balcony%20railing%20at%20night%20with%20a%20cigarette%20unlit%20in%20hand%2C%20warm%20interior%20light%20spilling%20out%2C%20amber%20and%20charcoal%20tones%2C%20contemplative%20mood%2C%20film%20photography&width=800&height=500&seq=actor-zhou-muchen-still-01&orientation=landscape",
    ],
    body: [
      {
        heading: "演员简介",
        paragraphs: [
          "周慕辰，1995年生，上海戏剧学院毕业。以都市剧中的“松弛感青年”形象被观众熟知，此次在剧中承担了大量喜剧节奏与关键转折。",
        ],
      },
      {
        heading: "角色解读",
        paragraphs: [
          "沈明远是家里唯一敢在饭桌上说真话的人。他的玩世不恭其实是一种保护色：看得最清楚，所以最不愿意被卷进去。第7集他一句“妈，你别再演了”，把整部剧推向转折。",
        ],
      },
      {
        heading: "代表作品",
        paragraphs: [
          "《我们的第七年》饰 江野；《小城大事》饰 陈放；《嫂子的诱惑》饰 沈明远。",
        ],
      },
    ],
  },
  {
    id: "actor-05",
    type: "actor",
    title: "许知遥",
    subtitle: "饰 林知夏",
    role: "林知夏",
    roleType: "特别出演",
    birthday: "1990-09-30",
    excerpt:
      "饰演沈嘉言的旧爱林知夏。她不是反派，而是一面镜子——照出这个家所有没被说出口的“如果”。",
    cover:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20poised%20Chinese%20actress%20in%20her%20mid%20thirties%20with%20sleek%20straight%20shoulder-length%20hair%20and%20confident%20calm%20gaze%2C%20wearing%20a%20cream%20trench%20coat%20over%20a%20black%20top%2C%20warm%20neutral%20studio%20backdrop%2C%20editorial%20portrait%20photography%2C%20soft%20diffused%20lighting&width=600&height=600&seq=actor-xu-zhiyao-01&orientation=squarish",
    date: "2026-08-05",
    tags: ["演员资料", "特别出演", "林知夏", "许知遥"],
    stills: [
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20an%20elegant%20Chinese%20woman%20in%20a%20long%20trench%20coat%20standing%20beside%20a%20black%20sedan%20on%20a%20rainy%20night%20street%2C%20reflected%20neon%20and%20warm%20headlights%2C%20deep%20burgundy%20and%20amber%20tones%2C%20cinematic%20mood&width=800&height=500&seq=actor-xu-zhiyao-still-01&orientation=landscape",
    ],
    body: [
      {
        heading: "演员简介",
        paragraphs: [
          "许知遥，1990年生，毕业于中国传媒大学，后赴伦敦进修表演。以电影《南方来信》获得多个新人奖项，近年多活跃于文艺片与剧集特别出演。",
        ],
      },
      {
        heading: "角色解读",
        paragraphs: [
          "林知夏最难演的地方在于“不能有一丝敌意”。许知遥的处理方式是让角色始终站在门口、门边、楼梯口——她永远是那个随时可以离开的人，因此反而最自由。",
        ],
      },
      {
        heading: "代表作品",
        paragraphs: [
          "《南方来信》饰 阿宁；《潮汐之外》饰 沈青；《嫂子的诱惑》饰 林知夏。",
        ],
      },
    ],
  },
  {
    id: "actor-06",
    type: "actor",
    title: "罗恒",
    subtitle: "饰 老徐",
    role: "老徐",
    roleType: "配角",
    birthday: "1972-01-15",
    excerpt:
      "饰演沈家多年的司机老徐。台词不多，却是唯一见证沈家三十年变迁的人，也是剧中最温柔的一双眼睛。",
    cover:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20kind%20Chinese%20man%20in%20his%20early%20fifties%20with%20short%20greying%20hair%20and%20gentle%20weathered%20face%2C%20wearing%20a%20dark%20grey%20driver%20uniform%20jacket%2C%20warm%20neutral%20studio%20background%2C%20editorial%20portrait%20photography%2C%20soft%20warm%20lighting&width=600&height=600&seq=actor-luo-heng-01&orientation=squarish",
    date: "2026-08-06",
    tags: ["演员资料", "配角", "老徐", "罗恒"],
    stills: [
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20middle-aged%20Chinese%20driver%20polishing%20a%20vintage%20black%20car%20in%20the%20courtyard%20of%20an%20old%20mansion%20at%20golden%20hour%2C%20warm%20amber%20light%2C%20nostalgic%20quiet%20mood%2C%20film%20photography&width=800&height=500&seq=actor-luo-heng-still-01&orientation=landscape",
    ],
    body: [
      {
        heading: "演员简介",
        paragraphs: [
          "罗恒，1972年生，资深配角演员，出演过六十余部影视作品。以生活化、无痕迹的表演风格著称，常被称为“剧集的定海神针”。",
        ],
      },
      {
        heading: "角色解读",
        paragraphs: [
          "老徐知道沈家所有的秘密，但他选择做一个安静的旁观者。第8集他开车送一家人去医院，路上说了一句“路我熟”，是全剧最朴素的安慰。",
        ],
      },
      {
        heading: "代表作品",
        paragraphs: [
          "《老城故事》饰 老周；《三十里铺》饰 王大勇；《嫂子的诱惑》饰 老徐。",
        ],
      },
    ],
  },
];

export const newsItems: NewsItem[] = [
  {
    id: "news-01",
    type: "news",
    title: "《嫂子的诱惑》收视破2，家庭题材为何再度成为爆款",
    subtitle: "收视动态 · 连续三周同时段第一",
    source: "影视观察",
    excerpt:
      "开播第三周，《嫂子的诱惑》在同时段收视率突破2.1%，网络平台热度值连续七日位居榜首。没有强情节反转的家庭剧，靠什么留住了观众？",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20cozy%20living%20room%20with%20a%20television%20glowing%20warmly%20in%20the%20evening%2C%20family%20silhouettes%20watching%2C%20deep%20amber%20and%20burgundy%20tones%2C%20soft%20film%20grain%2C%20editorial%20photography&width=800&height=500&seq=news-cover-01&orientation=landscape",
    date: "2026-08-16",
    tags: ["影视资讯", "收视动态", "数据", "家庭剧"],
    body: [
      {
        heading: "数据背后的观众",
        paragraphs: [
          "根据第三方数据平台统计，《嫂子的诱惑》开播第三周在同时段收视率突破2.1%，网络平台热度值连续七日位居榜首，讨论量集中于“餐桌戏”和“沉默”两个关键词。",
          "值得注意的是，观众构成中35岁以上人群占比接近六成，但18至24岁观众的比例逐周上升。这意味着剧集正在突破家庭剧的固有受众边界。",
        ],
      },
      {
        heading: "为什么是它",
        paragraphs: [
          "与近年流行的强冲突家庭剧不同，《嫂子的诱惑》几乎不制造戏剧化的爆点。它把冲突放在煮茶、留灯、切水果这些日常动作里，让观众在细节中自行辨认情绪。",
          "剧评人指出，这种“低音量叙事”恰好契合了当下观众的观看习惯：不追求即时刺激，但愿意在剧集结束后反复回想。",
        ],
      },
      {
        heading: "后续走势",
        paragraphs: [
          "随着第7集真相揭晓，剧集口碑进一步走高。平台方表示，大结局将采用不加更、不点映的方式与观众见面，希望保留完整的追剧体验。",
        ],
      },
    ],
  },
  {
    id: "news-02",
    type: "news",
    title: "导演专访：我想拍的不是婆媳大战，而是一个家怎么学会说话",
    subtitle: "主创专访 · 关于克制与留白",
    source: "剧集手册",
    excerpt:
      "导演在专访中谈到，全剧删掉了近四十分钟的争吵戏，“因为真正的伤害往往不是喊出来的”。他还透露了那个反复出现的糖罐的来历。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20film%20directors%20chair%20and%20monitor%20on%20a%20warmly%20lit%20interior%20set%20of%20a%20Chinese%20family%20drama%2C%20soft%20amber%20practical%20lights%2C%20deep%20red%20curtains%2C%20behind-the-scenes%20editorial%20photography%2C%20shallow%20depth%20of%20field&width=800&height=500&seq=news-cover-02&orientation=landscape",
    date: "2026-08-14",
    tags: ["影视资讯", "主创专访", "导演", "幕后"],
    body: [
      {
        heading: "删掉四十分钟的争吵",
        paragraphs: [
          "“最开始剪出来的版本里，第5集有一场很长的争吵，从餐桌吵到院子。”导演说，“后来我全部删掉了。因为真正的伤害往往不是喊出来的，是喊完之后那几分钟的安静。”",
          "最终呈现的版本里，那场戏只剩下筷子放下的声音和一句没有回答的问句。导演认为，观众的想象力比任何台词都更有力量。",
        ],
      },
      {
        heading: "糖罐是怎么来的",
        paragraphs: [
          "糖罐是道具组在旧货市场找到的。导演看到它的第一眼就决定要给这个罐子一条完整的线索：从第2集被收进柜子，到第8集重新出现在桌上。",
          "“它不解释任何事，但它替角色说了很多话。”",
        ],
      },
      {
        heading: "关于结局",
        paragraphs: [
          "导演强调，结局并不是大团圆。“沈母还要复查，苏婉也回到了学校，问题没有被解决，只是这家人终于愿意开口了。对我来说，这已经足够。”",
        ],
      },
    ],
  },
  {
    id: "news-03",
    type: "news",
    title: "林婉清谈苏婉：我把她的台词删掉了一半",
    subtitle: "主创专访 · 演员的二次创作",
    source: "光影访谈",
    excerpt:
      "在接受采访时，林婉清透露自己在拍摄前与导演商量删减了大量台词。“苏婉不是一个善于表达的人，如果我一直在说，观众就不会心疼她。”",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20behind-the-scenes%20still%20of%20a%20Chinese%20actress%20reviewing%20a%20script%20in%20a%20quiet%20dressing%20room%20with%20mirror%20bulbs%2C%20warm%20amber%20light%2C%20deep%20burgundy%20upholstered%20chair%2C%20editorial%20photography%2C%20intimate%20mood&width=800&height=500&seq=news-cover-03&orientation=landscape",
    date: "2026-08-12",
    tags: ["影视资讯", "主创专访", "林婉清", "表演"],
    body: [
      {
        heading: "从台词到眼神",
        paragraphs: [
          "林婉清说，进组第一天她就向导演提出，希望能把苏婉的部分台词交给动作。“她在这个家里说话的机会本来就不多，每一句都应该是有分量的。”",
          "于是观众看到了大量没有台词的处理：把糖罐收起来、关掉客厅的灯、在厨房里停住刀。",
        ],
      },
      {
        heading: "最难的一场戏",
        paragraphs: [
          "她提到第8集走廊里的拥抱。“那场戏我们拍了七条。前六条我都在哭，导演说不对，苏婉不会在这个时候哭，她只会怔住。第七条，我什么都没做，只是站着。”",
        ],
      },
      {
        heading: "关于观众的评价",
        paragraphs: [
          "面对“最隐忍儿媳”的评价，她笑了笑：“我不觉得她在忍。她一直在等一个可以说话的时刻。这个角色教我的，是耐心。”",
        ],
      },
    ],
  },
  {
    id: "news-04",
    type: "news",
    title: "取景地盘点：沈家老宅到底在哪里",
    subtitle: "幕后花絮 · 一栋房子撑起一部剧",
    source: "影像地理",
    excerpt:
      "剧中沈家老宅的庭院、阁楼与长餐桌，大部分戏份都在同一栋民国建筑内完成。我们整理了四处主要取景地，以及它们在剧中的对应场景。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20exterior%20of%20a%20historic%20Chinese%20mansion%20with%20grey%20brick%20walls%20and%20carved%20wooden%20windows%2C%20golden%20late%20afternoon%20light%2C%20autumn%20trees%2C%20warm%20amber%20and%20muted%20ochre%20tones%2C%20architectural%20editorial%20photography&width=800&height=500&seq=news-cover-04&orientation=landscape",
    date: "2026-08-10",
    tags: ["影视资讯", "幕后花絮", "取景地", "美术"],
    body: [
      {
        heading: "主场景：城西民国宅院",
        paragraphs: [
          "沈家的主场景位于城西一栋建于上世纪三十年代的宅院。美术组保留了原有的木地板与花砖，只重新布置了家具与灯光。",
          "剧中反复出现的长餐桌，是美术组按照原宅 dining room 的比例定制的，长约三米二，正好能容纳五个人各坐一端。",
        ],
      },
      {
        heading: "阁楼与楼梯",
        paragraphs: [
          "第3集的阁楼戏在同一栋建筑的顶层拍摄。由于空间狭小，摄影机只能使用手持和长焦，这也意外地强化了那场戏的局促感。",
        ],
      },
      {
        heading: "医院走廊",
        paragraphs: [
          "大结局的医院走廊并非实景，而是在棚内搭建。导演要求走廊必须足够长，让等待显得真实。“三个小时，观众要能感觉到长度。”",
        ],
      },
    ],
  },
  {
    id: "news-05",
    type: "news",
    title: "原声带上线：一段大提琴，写了三个月的沉默",
    subtitle: "幕后花絮 · 配乐特辑",
    source: "乐幕",
    excerpt:
      "《嫂子的诱惑》原声带正式上线，共收录18首配乐。主题曲《留一盏灯》以一段低音大提琴独奏开场，作曲称这是“为不说话的人写的音乐”。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20cello%20resting%20against%20a%20wooden%20chair%20in%20a%20warm%20recording%20studio%2C%20sheet%20music%20on%20a%20stand%2C%20amber%20lamp%20glow%2C%20deep%20burgundy%20acoustic%20panels%2C%20editorial%20music%20photography%2C%20soft%20film%20grain&width=800&height=500&seq=news-cover-05&orientation=landscape",
    date: "2026-08-08",
    tags: ["影视资讯", "幕后花絮", "原声带", "配乐"],
    body: [
      {
        heading: "为沉默写音乐",
        paragraphs: [
          "原声带共收录18首配乐，其中主题曲《留一盏灯》由低音大提琴独奏开场，几乎没有旋律起伏，只有呼吸般的运弓。",
          "作曲在特辑中说：“这部剧最重要的情绪都是没说出口的，所以音乐不能抢戏，它应该像一个在门外等你的人。”",
        ],
      },
      {
        heading: "三首必听曲目",
        paragraphs: [
          "《糖罐》以钢琴单音贯穿，对应第2集的关键道具；《雨夜》用弦乐颤音模拟雨声；《路我熟》是全剧唯一使用温暖和声的曲子，出现在大结局。",
        ],
      },
      {
        heading: "上线信息",
        paragraphs: [
          "原声带已在主流音乐平台上线，同时发行实体黑胶版本，附赠剧照册与主创手记。",
        ],
      },
    ],
  },
  {
    id: "news-06",
    type: "news",
    title: "大结局口碑出炉：没有大团圆，却让观众集体沉默",
    subtitle: "收视动态 · 收官讨论",
    source: "影视观察",
    excerpt:
      "大结局播出当晚，“嫂子的诱惑结局”登上热搜。观众讨论最多的不是谁对谁错，而是那句“糖罐在第二个柜子”。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20an%20empty%20family%20dining%20room%20at%20dawn%20with%20a%20single%20porcelain%20teacup%20and%20a%20sugar%20jar%20on%20the%20table%2C%20soft%20morning%20light%20through%20sheer%20curtains%2C%20warm%20cream%20and%20burgundy%20tones%2C%20quiet%20poetic%20mood%2C%20film%20photography&width=800&height=500&seq=news-cover-06&orientation=landscape",
    date: "2026-08-18",
    tags: ["影视资讯", "收视动态", "大结局", "口碑"],
    body: [
      {
        heading: "收官数据",
        paragraphs: [
          "大结局当晚，剧集在网络平台的热度值达到开播以来最高点，相关话题阅读量超过八亿。与高热度形成对比的是，讨论区的情绪并不激烈，更多是克制的长评。",
        ],
      },
      {
        heading: "观众在讨论什么",
        paragraphs: [
          "被提及最多的是沈母交钥匙的那场戏。有观众写道：“她给的从来不是钥匙，是承认。”也有人认为结局过于温和，希望看到更明确的道歉。",
          "对此编剧回应：“道歉在这部剧里是最容易写的一句台词，但我不想让和解变得太便宜。”",
        ],
      },
      {
        heading: "是否有第二季",
        paragraphs: [
          "制作方表示暂不开发续集，但会考虑以番外形式补完沈明远与老徐的故事线。",
        ],
      },
    ],
  },
];

export const contentItems: ContentItem[] = [...dramas, ...actors, ...newsItems];

export const latestDrama = dramas[dramas.length - 1];

export const featuredIds = ["drama-08", "drama-05", "actor-01", "news-01"];